import { ElementType } from 'react';

import { PresentationPageKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

/**
 * Education slide categories for organizing custom slides.
 * This is the single source of truth for category values.
 */
export const PLANNING_BASICS_CATEGORY = 'Planning basics' as const;
export const PLANNING_STRATEGIES_CATEGORY = 'Planning strategies' as const;
export const STATE_INHERITANCE_TAX_CATEGORY =
  'States with estate or inheritance tax' as const;
export const TAX_TOPICS_CATEGORY = 'Tax topics' as const;

export const EDUCATION_CATEGORIES = [
  PLANNING_BASICS_CATEGORY,
  PLANNING_STRATEGIES_CATEGORY,
  TAX_TOPICS_CATEGORY,
  STATE_INHERITANCE_TAX_CATEGORY,
] as const;

/**
 * Type for education slide categories.
 */
export type EducationCategory = (typeof EDUCATION_CATEGORIES)[number];

import {
  CltOverviewPageOne,
  CltOverviewPageTwo,
} from '../presentation/clientPresentation/components/slides/EducationalSlide/content/CltOverview';
import {
  CrtOverviewPageOne,
  CrtOverviewPageTwo,
} from '../presentation/clientPresentation/components/slides/EducationalSlide/content/CrtOverview';
import { Draft } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/Draft/Draft';
import { EstatePlanningChecklist } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/EstatePlanningChecklist/EstatePlanningChecklist';
import { EstatePlanningGlossary } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/EstatePlanningGlossary/EstatePlanningGlossary';
import { EstateTaxesExplainer } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/EstateTaxesExplainer/EstateTaxesExplainer';
import { FlpOverviewPageOne } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/FlpOverview/FlpOverviewPageOne';
import { FlpOverviewPageTwo } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/FlpOverview/FlpOverviewPageTwo';
import { GratOverviewPageOne } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/GratOverview/GratOverviewPageOne';
import { GratOverviewPageTwo } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/GratOverview/GratOverviewPageTwo';
import { GstExplainer } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/GstExplainer/GstExplainer';
import { IdgtOverviewPageOne } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/IdgtOverview/IdgtOverviewPageOne';
import { IlitOverviewPageOne } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/IlitOverview/IlitOverviewPageOne';
import { IlitOverviewPageTwo } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/IlitOverview/IlitOverviewPageTwo';
import { InstallmentSaleOverviewPageOne } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/InstallmentSaleOverview/InstallmentSaleOverviewPageOne';
import { InstallmentSaleOverviewPageTwo } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/InstallmentSaleOverview/InstallmentSaleOverviewPageTwo';
import { ManagingRecentInheritance } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/ManagingRecentInheritance/ManagingRecentInheritance';
import { NoEstatePlanningConsequences } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/NoEstatePlanningConsequences/NoEstatePlanningConsequences';
import { PhilanthropicPlanning } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/PhilanthropicPlanning/PhilanthropicPlanning';
import { PopularEstatePlanningStrategies } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/PopularEstatePlanningStrategies/PopularEstatePlanningStrategies';
import { QprtOverviewPageOne } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/QprtOverview/QprtOverviewPageOne';
import { QprtOverviewPageTwo } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/QprtOverview/QprtOverviewPageTwo';
import { SettingUpEstatePlan } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/SettingUpEstatePlan/SettingUpEstatePlan';
import { SlatOverviewPageOne } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/SlatOverview/SlatOverviewPageOne';
import { SlatOverviewPageTwo } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/SlatOverview/SlatOverviewPageTwo';
import { StateInheritanceTaxExplainer } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/StateInheritanceTaxExplainer/StateInheritanceTaxExplainer';
import { TrustBasics } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/TrustBasics/TrustBasics';
import { WhyEstatePlanningMatters } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/WhyEstatePlanningMatters/WhyEstatePlanningMatters';
import { WhyUseTrust } from '../presentation/clientPresentation/components/slides/EducationalSlide/content/WhyUseTrust/WhyUseTrust';
import { DRAFT_SLIDE_ID, DraftSlideId } from './Education.sentinels';

export enum EducationSlideType {
  WhyEstatePlanningMatters = 'WHY_ESTATE_PLANNING_MATTERS',
  NoEstatePlanningConsequences = 'NO_ESTATE_PLANNING_CONSEQUENCES',
  EstateTaxesExplainer = 'ESTATE_TAXES_EXPLAINER',
  StateInheritanceTaxExplainer = 'STATE_INHERITANCE_TAX_EXPLAINER',

  ManagingRecentInheritance = 'MANAGING_RECENT_INHERITANCE',
  EstatePlanningGlossary = 'ESTATE_PLANNING_GLOSSARY',
  SettingUpEstatePlan = 'SETTING_UP_ESTATE_PLAN',
  EstatePlanningChecklist = 'ESTATE_PLANNING_CHECKLIST',
  GstExplainer = 'GST_EXPLAINER',
}

export enum PlanningStrategiesSlideType {
  WhyUseTrust = 'WHY_USE_TRUST',
  TrustBasics = 'TRUST_BASICS',
  Clt = 'CLT',
  Crt = 'CRT',
  Grat = 'GRAT',
  PopularEstatePlanningStrategies = 'POPULAR_ESTATE_PLANNING_STRATEGIES',
  InstallmentSaleLoan = 'INSTALLMENT_SALE_LOAN',
  Ilit = 'ILIT',
  Slat = 'SLAT',
  Qprt = 'QPRT',
  Idgt = 'IDGT',
  Flp = 'FLP',
  PhilanthropicPlanning = 'PHILANTHROPIC_PLANNING',
}

export type EducationListItemType =
  | EducationSlideType
  | PlanningStrategiesSlideType
  | StateTaxEducationSlideType
  | DraftSlideId;

export interface EducationListItem {
  title: string;
  id: EducationListItemType;
  slides: ElementType[];
}

export const STATE_TAX_ITEMS = [
  PresentationPageKind.ConnecticutEstateAndGiftTax,
  PresentationPageKind.DistrictOfColumbiaTax,
  PresentationPageKind.HawaiiEstateTax,
  PresentationPageKind.IllinoisEstateTax,
  PresentationPageKind.KentuckyInheritanceTax,
  PresentationPageKind.MaineEstateTax,
  PresentationPageKind.MarylandEstateAndInheritanceTax,
  PresentationPageKind.MassachusettsEstateTax,
  PresentationPageKind.MinnesotaEstateTax,
  PresentationPageKind.NebraskaInheritanceTax,
  PresentationPageKind.NewJerseyInheritanceTax,
  PresentationPageKind.NewYorkEstateTax,
  PresentationPageKind.OregonEstateTax,
  PresentationPageKind.PennsylvaniaInheritanceTax,
  PresentationPageKind.RhodeIslandEstateTax,
  PresentationPageKind.VermontEstateTax,
  PresentationPageKind.WashingtonEstateTax,
] as const;

export type StateTaxEducationSlideType = (typeof STATE_TAX_ITEMS)[number];

const STATE_TAX_EDUCATION_TITLES: Record<StateTaxEducationSlideType, string> = {
  [PresentationPageKind.ConnecticutEstateAndGiftTax]:
    'Connecticut estate & gift tax',
  [PresentationPageKind.DistrictOfColumbiaTax]: 'District of Columbia tax',
  [PresentationPageKind.HawaiiEstateTax]: 'Hawaii estate tax',
  [PresentationPageKind.IllinoisEstateTax]: 'Illinois estate tax',
  [PresentationPageKind.KentuckyInheritanceTax]: 'Kentucky inheritance tax',
  [PresentationPageKind.MaineEstateTax]: 'Maine estate tax',
  [PresentationPageKind.MarylandEstateAndInheritanceTax]:
    'Maryland estate & inheritance tax',
  [PresentationPageKind.MassachusettsEstateTax]: 'Massachusetts estate tax',
  [PresentationPageKind.MinnesotaEstateTax]: 'Minnesota estate tax',
  [PresentationPageKind.NebraskaInheritanceTax]: 'Nebraska inheritance tax',
  [PresentationPageKind.NewJerseyInheritanceTax]: 'New Jersey inheritance tax',
  [PresentationPageKind.NewYorkEstateTax]: 'New York estate tax',
  [PresentationPageKind.OregonEstateTax]: 'Oregon estate tax',
  [PresentationPageKind.PennsylvaniaInheritanceTax]:
    'Pennsylvania inheritance tax',
  [PresentationPageKind.RhodeIslandEstateTax]: 'Rhode Island estate tax',
  [PresentationPageKind.VermontEstateTax]: 'Vermont estate tax',
  [PresentationPageKind.WashingtonEstateTax]: 'Washington estate tax',
};

const STATE_TAX_EDUCATION_MAP = STATE_TAX_ITEMS.reduce<
  Record<StateTaxEducationSlideType, EducationListItem>
>(
  (educationMap, id) => {
    educationMap[id] = {
      id,
      title: STATE_TAX_EDUCATION_TITLES[id],
      slides: [Draft],
    };
    return educationMap;
  },
  {} as Record<StateTaxEducationSlideType, EducationListItem>
);

/**
 * Internal map of education slide data. Access via public getters:
 * - `getEducationSlides(slideType)` - Get slide components for a slide type
 * - `getDefaultEducationTitle(slideType)` - Get default title for a slide type
 * - `getEducationSlideIds()` - Get all slide type IDs
 * - `useEducationMap()` - Hook to get the full map with tenant overrides (from context)
 */
const EDUCATION_MAP: Record<EducationListItemType, EducationListItem> = {
  [EducationSlideType.WhyEstatePlanningMatters]: {
    title: 'Why estate planning matters',
    id: EducationSlideType.WhyEstatePlanningMatters,
    slides: [WhyEstatePlanningMatters],
  },
  [EducationSlideType.NoEstatePlanningConsequences]: {
    title: 'Potential consequences of not doing proper planning',
    id: EducationSlideType.NoEstatePlanningConsequences,
    slides: [NoEstatePlanningConsequences],
  },
  [EducationSlideType.EstateTaxesExplainer]: {
    title: 'Gift and estate taxes',
    id: EducationSlideType.EstateTaxesExplainer,
    slides: [EstateTaxesExplainer],
  },
  [EducationSlideType.StateInheritanceTaxExplainer]: {
    title: 'State estate and inheritance taxes',
    id: EducationSlideType.StateInheritanceTaxExplainer,
    slides: [StateInheritanceTaxExplainer],
  },
  ...STATE_TAX_EDUCATION_MAP,
  [PlanningStrategiesSlideType.PopularEstatePlanningStrategies]: {
    title: 'Popular estate planning strategies to consider',
    id: PlanningStrategiesSlideType.PopularEstatePlanningStrategies,
    slides: [PopularEstatePlanningStrategies],
  },
  [EducationSlideType.ManagingRecentInheritance]: {
    title: 'Smart strategies for managing your inheritance',
    id: EducationSlideType.ManagingRecentInheritance,
    slides: [ManagingRecentInheritance],
  },
  [EducationSlideType.EstatePlanningGlossary]: {
    title: 'Estate planning glossary: Key terms & documents',
    id: EducationSlideType.EstatePlanningGlossary,
    slides: [EstatePlanningGlossary],
  },
  [EducationSlideType.SettingUpEstatePlan]: {
    title: 'Setting up an estate plan',
    id: EducationSlideType.SettingUpEstatePlan,
    slides: [SettingUpEstatePlan],
  },
  [EducationSlideType.EstatePlanningChecklist]: {
    title: 'Estate planning checklist for major life events',
    id: EducationSlideType.EstatePlanningChecklist,
    slides: [EstatePlanningChecklist],
  },
  [EducationSlideType.GstExplainer]: {
    title: 'Generation-skipping transfer (GST) tax',
    id: EducationSlideType.GstExplainer,
    slides: [GstExplainer],
  },
  [PlanningStrategiesSlideType.WhyUseTrust]: {
    title: 'Why use a trust?',
    id: PlanningStrategiesSlideType.WhyUseTrust,
    slides: [WhyUseTrust],
  },
  [PlanningStrategiesSlideType.TrustBasics]: {
    title: 'Trust basics',
    id: PlanningStrategiesSlideType.TrustBasics,
    slides: [TrustBasics],
  },
  [PlanningStrategiesSlideType.Clt]: {
    title: 'Charitable Lead Trust (CLT)',
    id: PlanningStrategiesSlideType.Clt,
    slides: [CltOverviewPageOne, CltOverviewPageTwo],
  },
  [PlanningStrategiesSlideType.Crt]: {
    title: 'Charitable Remainder Trust (CRT)',
    id: PlanningStrategiesSlideType.Crt,
    slides: [CrtOverviewPageOne, CrtOverviewPageTwo],
  },
  [PlanningStrategiesSlideType.Grat]: {
    title: 'Grantor Retained Annuity Trust (GRAT)',
    id: PlanningStrategiesSlideType.Grat,
    slides: [GratOverviewPageOne, GratOverviewPageTwo],
  },
  [PlanningStrategiesSlideType.InstallmentSaleLoan]: {
    title: 'Installment Sale & Loan',
    id: PlanningStrategiesSlideType.InstallmentSaleLoan,
    slides: [InstallmentSaleOverviewPageOne, InstallmentSaleOverviewPageTwo],
  },
  [PlanningStrategiesSlideType.Ilit]: {
    title: 'Irrevocable Life Insurance Trust (ILIT)',
    id: PlanningStrategiesSlideType.Ilit,
    slides: [IlitOverviewPageOne, IlitOverviewPageTwo],
  },
  [PlanningStrategiesSlideType.Slat]: {
    title: 'Spousal Lifetime Access Trust (SLAT)',
    id: PlanningStrategiesSlideType.Slat,
    slides: [SlatOverviewPageOne, SlatOverviewPageTwo],
  },
  [PlanningStrategiesSlideType.Qprt]: {
    title: 'Qualified Personal Residence Trust (QPRT)',
    id: PlanningStrategiesSlideType.Qprt,
    slides: [QprtOverviewPageOne, QprtOverviewPageTwo],
  },
  [PlanningStrategiesSlideType.Idgt]: {
    title: 'Intentionally Defective Grantor Trust (IDGT)',
    id: PlanningStrategiesSlideType.Idgt,
    slides: [IdgtOverviewPageOne],
  },
  [PlanningStrategiesSlideType.Flp]: {
    title: 'Family Limited Partnership (FLP)',
    id: PlanningStrategiesSlideType.Flp,
    slides: [FlpOverviewPageOne, FlpOverviewPageTwo],
  },
  [PlanningStrategiesSlideType.PhilanthropicPlanning]: {
    title:
      'Philanthropic Planning: Donor-Advised Funds and Private Foundations',
    id: PlanningStrategiesSlideType.PhilanthropicPlanning,
    slides: [PhilanthropicPlanning],
  },
  [DRAFT_SLIDE_ID]: {
    title: 'Custom page',
    id: DRAFT_SLIDE_ID,
    slides: [Draft],
  },
};

/**
 * Gets the slide components for an education slide type.
 * @param slideType - The education slide type
 * @returns Array of slide components, or undefined if slide type not found
 */
export function getEducationSlides(
  slideType: EducationListItemType
): ElementType[] | undefined {
  return EDUCATION_MAP[slideType]?.slides;
}

/**
 * Gets the default title for an education slide type.
 * Note: For tenant-overridden titles, use `useEducationMap()` hook from the context.
 * @param slideType - The education slide type
 * @returns The default title, or undefined if slide type not found
 */
export function getDefaultEducationTitle(
  slideType: EducationListItemType
): string | undefined {
  const title = EDUCATION_MAP[slideType]?.title;

  if (title === undefined) {
    diagnostics.warn(
      `No education slide found for type "${slideType}" - this may indicate an invalid slide type or missing EDUCATION_MAP entry`
    );
  }

  return title;
}

/**
 * Gets all education slide type IDs.
 * @returns Array of all education slide type IDs
 */
export function getEducationSlideIds(): EducationListItemType[] {
  return Object.keys(EDUCATION_MAP) as EducationListItemType[];
}

/**
 * Gets the full education list item (slides, title, id) for internal use.
 * Note: For consumer code, prefer using specific getters or `useEducationMap()` hook.
 * @internal
 */
export function getEducationListItem(
  slideType: EducationListItemType
): EducationListItem | undefined {
  return EDUCATION_MAP[slideType];
}

// IMPORTANT: The order of checkboxes in AddEducationPagesModal.tsx should match this order
export const PLANNING_STRATEGIES_ITEMS: EducationListItemType[] = [
  PlanningStrategiesSlideType.WhyUseTrust,
  PlanningStrategiesSlideType.TrustBasics,
  PlanningStrategiesSlideType.PopularEstatePlanningStrategies,
  PlanningStrategiesSlideType.Clt,
  PlanningStrategiesSlideType.Crt,
  PlanningStrategiesSlideType.Flp,
  PlanningStrategiesSlideType.Grat,
  PlanningStrategiesSlideType.Idgt,
  PlanningStrategiesSlideType.InstallmentSaleLoan,
  PlanningStrategiesSlideType.Ilit,
  PlanningStrategiesSlideType.Slat,
  PlanningStrategiesSlideType.Qprt,
  PlanningStrategiesSlideType.PhilanthropicPlanning,
];

// Education admin page categories
export const ABOUT_ESTATE_PLANNING_ITEMS: EducationSlideType[] = [
  EducationSlideType.EstatePlanningGlossary,
  EducationSlideType.WhyEstatePlanningMatters,
  EducationSlideType.NoEstatePlanningConsequences,
  EducationSlideType.SettingUpEstatePlan,
  EducationSlideType.EstatePlanningChecklist,
  EducationSlideType.ManagingRecentInheritance,
];

export const TAX_TOPICS_ITEMS: EducationSlideType[] = [
  EducationSlideType.EstateTaxesExplainer,
  EducationSlideType.StateInheritanceTaxExplainer,
  EducationSlideType.GstExplainer,
];

export const EDUCATION_ITEMS_BY_CATEGORY: Record<
  EducationCategory,
  readonly EducationListItemType[]
> = {
  [PLANNING_BASICS_CATEGORY]: ABOUT_ESTATE_PLANNING_ITEMS,
  [PLANNING_STRATEGIES_CATEGORY]: PLANNING_STRATEGIES_ITEMS,
  [TAX_TOPICS_CATEGORY]: TAX_TOPICS_ITEMS,
  [STATE_INHERITANCE_TAX_CATEGORY]: STATE_TAX_ITEMS,
};

/**
 * Maps PresentationPageKind to EducationListItemType for getting default data.
 */
export const PAGE_KIND_TO_EDUCATION_SLIDE_ID: Partial<
  Record<PresentationPageKind, EducationListItemType>
> = {
  [PresentationPageKind.NoEstatePlanningConsequences]:
    EducationSlideType.NoEstatePlanningConsequences,
  [PresentationPageKind.EstateTaxesExplainer]:
    EducationSlideType.EstateTaxesExplainer,
  [PresentationPageKind.StateInheritanceTaxExplainer]:
    EducationSlideType.StateInheritanceTaxExplainer,
  ...Object.fromEntries(
    STATE_TAX_ITEMS.map((pageKind) => [pageKind, pageKind])
  ),
  [PresentationPageKind.ManagingRecentInheritance]:
    EducationSlideType.ManagingRecentInheritance,
  [PresentationPageKind.WhyEstatePlanningMatters]:
    EducationSlideType.WhyEstatePlanningMatters,
  [PresentationPageKind.PopularPlanningStrategies]:
    PlanningStrategiesSlideType.PopularEstatePlanningStrategies,
  [PresentationPageKind.WhyUseTrust]: PlanningStrategiesSlideType.WhyUseTrust,
  [PresentationPageKind.TrustBasics]: PlanningStrategiesSlideType.TrustBasics,
  [PresentationPageKind.EstatePlanningGlossary]:
    EducationSlideType.EstatePlanningGlossary,
  [PresentationPageKind.SettingUpEstatePlan]:
    EducationSlideType.SettingUpEstatePlan,
  [PresentationPageKind.EstatePlanningChecklist]:
    EducationSlideType.EstatePlanningChecklist,
  [PresentationPageKind.GstExplainer]: EducationSlideType.GstExplainer,
  [PresentationPageKind.CltStrategyExplainer]: PlanningStrategiesSlideType.Clt,
  [PresentationPageKind.CrtStrategyExplainer]: PlanningStrategiesSlideType.Crt,
  [PresentationPageKind.GratStrategyExplainer]:
    PlanningStrategiesSlideType.Grat,
  [PresentationPageKind.IlitStrategyExplainer]:
    PlanningStrategiesSlideType.Ilit,
  [PresentationPageKind.InstallmentSaleStrategyExplainer]:
    PlanningStrategiesSlideType.InstallmentSaleLoan,
  [PresentationPageKind.SlatStrategyExplainer]:
    PlanningStrategiesSlideType.Slat,
  [PresentationPageKind.QprtStrategyExplainer]:
    PlanningStrategiesSlideType.Qprt,
  [PresentationPageKind.PhilanthropicPlanningExplainer]:
    PlanningStrategiesSlideType.PhilanthropicPlanning,
  [PresentationPageKind.IdgtStrategyExplainer]:
    PlanningStrategiesSlideType.Idgt,
  [PresentationPageKind.FlpStrategyExplainer]: PlanningStrategiesSlideType.Flp,
};
